if (!customElements.get('product-description')) {
  customElements.define(
    'product-description',
    class ProductDescription extends HTMLElement {
      abortController = null;
      resizeObserver = null;
      measureFrame = null;
      originalTabIndexes = new Map();
      isExpanded = false;

      connectedCallback() {
        if (this.abortController || this.dataset.collapsible !== 'true') return;

        this.content = this.querySelector('.product-description__content');
        this.contentInner = this.querySelector('.product-description__content-inner');
        this.toggleButton = this.querySelector('.product-description__toggle');
        if (!this.content || !this.contentInner || !this.toggleButton) return;

        this.abortController = new AbortController();
        this.toggleButton.addEventListener('click', this.onToggle.bind(this), {
          signal: this.abortController.signal,
        });
        window.addEventListener('resize', this.scheduleMeasure.bind(this), {
          passive: true,
          signal: this.abortController.signal,
        });

        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
          this.resizeObserver.observe(this.contentInner);
        }

        document.fonts?.ready.then(() => {
          if (this.isConnected) this.scheduleMeasure();
        });
        this.scheduleMeasure();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame);
        this.measureFrame = null;
        this.restoreFocusableElements();
      }

      scheduleMeasure() {
        if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame);
        this.measureFrame = window.requestAnimationFrame(() => {
          this.measureFrame = null;
          this.measure();
        });
      }

      measure() {
        const collapsedHeight = this.getCollapsedHeight();
        const contentHeight = Math.ceil(this.contentInner.scrollHeight);
        const shouldCollapse = collapsedHeight > 0 && contentHeight > collapsedHeight + 1;
        const wasCollapsible = this.classList.contains('is-collapsible');

        if (!shouldCollapse) {
          this.classList.remove('is-collapsible', 'is-expanded');
          this.toggleButton.hidden = true;
          this.toggleButton.setAttribute('aria-expanded', 'true');
          this.restoreFocusableElements();
          return;
        }

        this.classList.add('is-collapsible');
        if (!wasCollapsible) this.isExpanded = false;
        this.renderState();
      }

      getCollapsedHeight() {
        return Number.parseFloat(
          window.getComputedStyle(this).getPropertyValue('--product-description-collapsed-height')
        );
      }

      onToggle() {
        if (!this.classList.contains('is-collapsible')) return;

        const isCollapsing = this.isExpanded;
        this.isExpanded = !this.isExpanded;
        this.renderState();

        if (isCollapsing) {
          window.requestAnimationFrame(() => this.keepCollapsedDescriptionInView());
        }
      }

      renderState() {
        this.classList.toggle('is-expanded', this.isExpanded);
        this.toggleButton.hidden = false;
        this.toggleButton.setAttribute('aria-expanded', this.isExpanded ? 'true' : 'false');
        this.toggleButton.textContent = this.isExpanded
          ? this.toggleButton.dataset.labelCollapse
          : this.toggleButton.dataset.labelExpand;

        if (this.isExpanded) {
          this.restoreFocusableElements();
        } else {
          window.requestAnimationFrame(() => this.disableClippedFocusableElements());
        }
      }

      disableClippedFocusableElements() {
        if (this.isExpanded || !this.classList.contains('is-collapsible')) return;

        const contentBottom = this.content.getBoundingClientRect().bottom;
        const focusableElements = this.content.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, iframe, video[controls], audio[controls], [contenteditable="true"], [tabindex]'
        );

        focusableElements.forEach((element) => {
          const isClipped = element.getBoundingClientRect().bottom > contentBottom + 1;
          if (isClipped) {
            if (!this.originalTabIndexes.has(element)) {
              this.originalTabIndexes.set(element, element.getAttribute('tabindex'));
            }
            element.setAttribute('tabindex', '-1');
          } else {
            this.restoreFocusableElement(element);
          }
        });
      }

      restoreFocusableElement(element) {
        if (!this.originalTabIndexes.has(element)) return;

        const originalTabIndex = this.originalTabIndexes.get(element);
        if (originalTabIndex === null) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', originalTabIndex);
        }
        this.originalTabIndexes.delete(element);
      }

      restoreFocusableElements() {
        this.originalTabIndexes.forEach((originalTabIndex, element) => {
          if (originalTabIndex === null) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', originalTabIndex);
          }
        });
        this.originalTabIndexes.clear();
      }

      keepCollapsedDescriptionInView() {
        this.toggleButton.focus({ preventScroll: true });
        const descriptionRect = this.getBoundingClientRect();
        if (descriptionRect.bottom >= -window.innerHeight * 0.25) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const top = Math.max(0, window.scrollY + descriptionRect.top - 24);
        window.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
    }
  );
}
